import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useSession } from "@/app/session.tsx";
import { Button, Input, Panel } from "@/components/primitives.tsx";

/** The three seeded personas, so a reviewer does not have to be told them. */
const PERSONAS = [
  { username: "admin", role: "admin" },
  { username: "operator", role: "operator" },
  { username: "pengguna", role: "user" },
] as const;

export function LoginRoute() {
  const { t } = useTranslation();
  const { signIn, signInError, isSigningIn } = useSession();
  const [username, setUsername] = useState("pengguna");
  const [password, setPassword] = useState("");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await signIn(username.trim(), password);
    } catch {
      // Rendered from `signInError`; a rejected mutation here is not exceptional.
    }
  }

  return (
    <div className="login">
      <aside className="login-aside">
        <div>
          <div className="brand" style={{ marginBottom: "var(--s5)" }}>
            <span className="brand-mark" aria-hidden="true">
              PA
            </span>
            <span className="brand-name" style={{ fontSize: 19 }}>
              {t("app.name")}
            </span>
          </div>
          {/* The mockup promised "every answer names its source, or is not given" —
              an absolute the product breaks on three of its own screens. This is the
              claim it can actually keep. */}
          <p className="serif" style={{ fontSize: 17, lineHeight: 1.55, maxWidth: "30ch" }}>
            {t("login.promise")}
          </p>
        </div>
      </aside>

      <main className="login-main">
        <div className="login-box">
          <h1 className="page-title">{t("login.title")}</h1>
          <p className="page-sub">{t("login.subtitle")}</p>

          <form onSubmit={onSubmit} className="stack">
            <label className="stack" style={{ gap: 4 }}>
              <span className="eyebrow">{t("login.username")}</span>
              <Input
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </label>
            <label className="stack" style={{ gap: 4 }}>
              <span className="eyebrow">{t("login.password")}</span>
              <Input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>

            {signInError ? (
              <Panel tone="red" title={signInError} />
            ) : null}

            <Button type="submit" variant="primary" disabled={isSigningIn}>
              {isSigningIn ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          <div className="divider" />
          <div className="eyebrow">{t("login.personas")}</div>
          {/* What each account is FOR. A list of three usernames and their role codes
              tells a reviewer nothing about which one to pick. */}
          <p className="hint" style={{ margin: "4px 0 var(--s2)" }}>{t("login.personasHelp")}</p>
          <div className="stack">
            {PERSONAS.map((persona) => (
              <button
                key={persona.username}
                type="button"
                className="who"
                aria-pressed={username === persona.username}
                onClick={() => setUsername(persona.username)}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 600 }}>{persona.username}</span>
                  <span className="faint" style={{ fontSize: 12 }}>
                    {t(`login.persona_${persona.username}`)}
                  </span>
                </span>
                <span className="pill pill-bare mono" style={{ marginLeft: "auto" }}>
                  {persona.role}
                </span>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
